import React, { useEffect, useRef } from "react";
import "@/App.css";
import { ReactLenis } from "lenis/react";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { LanguageProvider } from "@/context/LanguageContext";
import { FavoritesProvider } from "@/context/FavoritesContext";
import { CartProvider } from "@/context/CartContext";
import Header from "@/components/layout/Header";
import HomePage from "@/pages/HomePage";
import FavoritesPage from "@/pages/FavoritesPage";
import OrderRequestPage from "@/pages/OrderRequestPage";
import OrderConfirmationPage from "@/pages/OrderConfirmationPage";
import VarietyPage from "@/pages/VarietyPage";
import CatalogPage from "@/pages/CatalogPage";
import BlogPage from "@/pages/BlogPage";
import PartnersPage from "@/pages/PartnersPage";
import AdminPage from "@/pages/AdminPage";
import LegalPage from "@/pages/LegalPage";
import ScrollToTop from "@/components/common/ScrollToTop";
import FloatingTray from "@/components/common/FloatingTray";
import { captureLanding, startInteractionTracking, trackPageview } from "@/lib/tracking";
import Ga4RouteTracker from "@/analytics/Ga4RouteTracker";
import { useLocation } from "react-router-dom";

// Task 23 — records every route change as a pageview for the heatmap.
// The admin panel is excluded — only visitor navigation counts.
function RouteTracker() {
  const location = useLocation();
  useEffect(() => {
    if (!location.pathname.startsWith("/admin")) {
      trackPageview(location.pathname);
    }
  }, [location.pathname]);
  return null;
}

function App() {
  const lenisRef = useRef(null);

  useEffect(() => {
    captureLanding(); // Task 21 — contact origin tracking
    startInteractionTracking(); // Task 23 — click heatmap
    const url = process.env.REACT_APP_BACKEND_URL;
    if (url) fetch(`${url}/api/health`).catch(() => {});
    const id = setInterval(() => {
      if (lenisRef.current?.lenis) {
        window.__lenis = lenisRef.current.lenis;
        clearInterval(id);
      }
    }, 200);
    return () => clearInterval(id);
  }, []);

  return (
    <LanguageProvider>
      <FavoritesProvider>
        <CartProvider>
          <ReactLenis
            ref={lenisRef}
            root
            options={{
              duration: 1.2,
              easing: (t) => Math.min(1, 1.001 - Math.pow(2, -10 * t)),
              smoothWheel: true,
            }}
          >
            <BrowserRouter>
              <Ga4RouteTracker />
              <RouteTracker />
              <div className="App bg-[#F6F2EC] min-h-screen">
                <Header />
                <Routes>
                  <Route path="/" element={<HomePage />} />
                  <Route path="/favorites" element={<FavoritesPage />} />
                  <Route path="/order-request" element={<OrderRequestPage />} />
                  <Route path="/order-request/confirmation/:ref" element={<OrderConfirmationPage />} />
                  <Route path="/variety/:slug" element={<VarietyPage />} />
                  <Route path="/catalog" element={<CatalogPage />} />
                  <Route path="/blog" element={<BlogPage />} />
                  <Route path="/partners" element={<PartnersPage />} />
                  <Route path="/privacy" element={<LegalPage doc="privacy" />} />
                  <Route path="/terms" element={<LegalPage doc="terms" />} />
                  <Route path="/admin" element={<AdminPage />} />
                  <Route path="*" element={<HomePage />} />
                </Routes>
                <Toaster position="bottom-left" />
                <ScrollToTop />
                <FloatingTray />
              </div>
            </BrowserRouter>
          </ReactLenis>
        </CartProvider>
      </FavoritesProvider>
    </LanguageProvider>
  );
}

export default App;
